import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Trophy, Users, Calendar, TrendingUp, Medal, Star } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/AppLayout';

const CompetitionsPage = () => {
  const { api, user } = useAuth();
  const [competitions, setCompetitions] = useState([]);
  const [myCompetitions, setMyCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('browse');

  useEffect(() => {
    loadCompetitions();
    loadMyCompetitions();
  }, []);

  const loadCompetitions = async () => {
    try {
      const res = await api.get('/competitions');
      setCompetitions(res.data);
    } catch (error) {
      console.error('Failed to load competitions:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMyCompetitions = async () => {
    try {
      const res = await api.get('/competitions/my-competitions');
      setMyCompetitions(res.data);
    } catch (error) {
      console.error('Failed to load my competitions:', error);
    }
  };

  const joinCompetition = async (id) => {
    try {
      await api.post(`/competitions/${id}/join`);
      loadCompetitions();
      loadMyCompetitions();
      alert('Joined competition!');
    } catch (error) {
      console.error('Failed to join:', error);
      alert(error.response?.data?.error || 'Failed to join competition');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString();
  };

  const getPrizeBadge = (rank) => {
    if (rank === 1) return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200"><Medal className="w-3 h-3 mr-1" /> Pro (1 yr)</Badge>;
    if (rank === 2) return <Badge className="bg-gray-100 text-gray-700 border-gray-200"><Medal className="w-3 h-3 mr-1" /> Pro (6 mo)</Badge>;
    if (rank === 3) return <Badge className="bg-amber-100 text-amber-700 border-amber-200"><Medal className="w-3 h-3 mr-1" /> Pro (3 mo)</Badge>;
    return null;
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="max-w-6xl mx-auto px-4 py-12 text-center">
          <div className="animate-pulse text-[#1A4D2E]">Loading competitions...</div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-serif text-3xl text-[#1F2937]">Trading Competitions</h1>
          <Button onClick={() => api.post('/competitions/create', {
            name: 'Weekly Challenge',
            startDate: new Date().toISOString().split('T')[0],
            endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            startingCapital: 10000,
            description: 'Weekly trading challenge'
          })}>
            Create Competition
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="browse">Browse Competitions</TabsTrigger>
            <TabsTrigger value="my">My Competitions</TabsTrigger>
          </TabsList>

          <TabsContent value="browse">
            {competitions.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {competitions.map((comp) => (
                  <Card key={comp.id} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">{comp.name}</CardTitle>
                        <Badge variant={comp.status === 'active' ? 'default' : 'outline'}>
                          {comp.status}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {comp.description || 'No description'}
                      </p>

                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2 text-gray-600">
                          <Calendar className="w-4 h-4" />
                          {formatDate(comp.startDate)} - {formatDate(comp.endDate)}
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Users className="w-4 h-4" />
                          Starting: {formatCurrency(parseFloat(comp.startingCapital))}
                        </div>
                      </div>

                      <div className="bg-stone-50 p-3 rounded-lg">
                        <p className="text-xs text-gray-500 mb-2">Prizes</p>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">1st:</span>
                            {getPrizeBadge(1)}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">2nd:</span>
                            {getPrizeBadge(2)}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">3rd:</span>
                            {getPrizeBadge(3)}
                          </div>
                        </div>
                      </div>

                      <Button
                        className="w-full"
                        onClick={() => joinCompetition(comp.id)}
                        disabled={comp.status !== 'active' && comp.status !== 'upcoming'}
                      >
                        {comp.status === 'active' ? 'Join Now' : comp.status === 'upcoming' ? 'Register' : 'Closed'}
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="p-12 text-center">
                  <Trophy className="w-16 h-16 mx-auto mb-4 text-[#1A4D2E] opacity-50" />
                  <h3 className="text-lg font-semibold text-[#1F2937] mb-2">No Active Competitions</h3>
                  <p className="text-gray-500">Check back soon for new trading competitions!</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="my">
            {myCompetitions.length > 0 ? (
              <div className="space-y-6">
                {myCompetitions.map((comp) => (
                  <Card key={comp.id}>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-lg text-[#1F2937]">{comp.name}</h3>
                            <Badge variant={comp.status === 'active' ? 'default' : 'outline'}>
                              {comp.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-500 mt-1">
                            {formatDate(comp.startDate)} - {formatDate(comp.endDate)}
                          </p>
                        </div>
                        <div className="text-right">
                          {comp.rank && (
                            <div className="flex items-center gap-2">
                              {comp.rank <= 3 ? (
                                <Medal className={`w-6 h-6 ${
                                  comp.rank === 1 ? 'text-yellow-500' :
                                  comp.rank === 2 ? 'text-gray-400' : 'text-amber-600'
                                }`} />
                              ) : (
                                <span className="text-2xl font-bold text-[#1A4D2E]">#{comp.rank}</span>
                              )}
                              <div>
                                <p className="text-sm text-gray-500">Portfolio Value</p>
                                <p className="text-xl font-bold text-[#1F2937]">
                                  {formatCurrency(comp.finalValue)}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {comp.status === 'active' && (
                        <div className="mt-4 pt-4 border-t border-stone-100">
                          <Button variant="outline" onClick={() => window.location.href = '/portfolio'}>
                            View Portfolio
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="p-12 text-center">
                  <Star className="w-16 h-16 mx-auto mb-4 text-[#1A4D2E] opacity-50" />
                  <h3 className="text-lg font-semibold text-[#1F2937] mb-2">No Active Entries</h3>
                  <p className="text-gray-500">Join a competition to start competing!</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
};

export default CompetitionsPage;
